'use client';

/**
 * FormEmbed (storefront, live) — renders a form designed in
 * Dashboard → Forms (a Puck document) inside any page.
 *
 * Fetches GET /api/forms/:formId (proxy → GET public/forms/:formId) and
 * renders the Puck document with a nested <PuckRenderer>. The form family
 * components in the storefront config are the LIVE wrappers — so the
 * embedded document renders a real, validating, submitting form.
 */

import React, { useEffect, useState } from 'react';
import type { Data } from '@puckeditor/core';
import { FormEmbed as SharedFormEmbed } from '@launchstore/shared-puck';
import type { FormEmbedProps } from '@launchstore/shared-puck';
import { resolveColor } from '@launchstore/shared-puck';
import PuckRenderer from '@/components/puck/PuckRenderer';

function FormEmbedLive({ formId, maxWidth, paddingX, paddingY, backgroundColor, borderRadius }: FormEmbedProps) {
  const [data, setData] = useState<Data | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'inactive'>('loading');

  useEffect(() => {
    if (!formId) return;
    let cancelled = false;
    setState('loading');
    fetch(`/api/forms/${encodeURIComponent(formId)}`)
      .then(async (res) => {
        const body = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setState(body?.message?.includes('not accepting') ? 'inactive' : 'error');
          return;
        }
        let parsed = body?.puckData;
        if (typeof parsed === 'string') {
          try { parsed = JSON.parse(parsed); } catch { parsed = null; }
        }
        if (parsed && Array.isArray((parsed as Data).content)) {
          setData(parsed as Data);
          setState('ready');
        } else {
          // Empty form (created but not designed yet) — nothing to render.
          setState('error');
        }
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [formId]);

  const shellClasses = [
    'w-full mx-auto',
    maxWidth && maxWidth !== 'full' ? `max-w-${maxWidth}` : '',
    `px-${paddingX || '0'} py-${paddingY || '0'}`,
  ].filter(Boolean).join(' ');

  const shellStyle = backgroundColor
    ? ({
        backgroundColor: resolveColor(backgroundColor) || undefined,
        borderRadius: borderRadius || 'lg',
      } as React.CSSProperties)
    : undefined;

  if (!formId) {
    return <>{(SharedFormEmbed.render as any)({ ...(SharedFormEmbed.defaultProps as FormEmbedProps), formId: '' })}</>;
  }

  if (state === 'loading') {
    return (
      <div className={shellClasses}>
        <div className="animate-pulse space-y-3 p-6">
          {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded bg-gray-200 dark:bg-gray-700" />)}
        </div>
      </div>
    );
  }

  if (state === 'inactive') {
    return (
      <div className={shellClasses}>
        <p className="py-8 text-center text-sm text-gray-500">This form is no longer accepting submissions.</p>
      </div>
    );
  }

  if (state === 'error' || !data) {
    return (
      <div className={shellClasses}>
        <p className="py-8 text-center text-sm text-gray-500">Form could not be loaded.</p>
      </div>
    );
  }

  return (
    <div className={shellClasses} style={shellStyle}>
      {/* Nested Puck render — zones in the form document resolve against it */}
      <PuckRenderer data={data} />
    </div>
  );
}

export const FormEmbed: any = {
  ...SharedFormEmbed,
  render: (props: FormEmbedProps) => <FormEmbedLive {...props} />,
};

export default FormEmbed;
